import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Select from 'fe-design-base/atoms/Select/Select';
import Text from 'fe-design-base/atoms/Text';
import { InputChangeEvent } from 'fe-design-base/baseTypes';
import Button from 'fe-design-base/molecules/Button';
import List from 'fe-design-base/molecules/List/List';
import Pill from 'fe-design-base/molecules/Pill/Pill';
import CardSelectable from 'fe-design-base/organisms/CardSelectable/CardSelectable';

import { showModal } from 'actions/modals';

import { HIRING_V2_SCHEDULE_CALL_MODAL } from 'features/hiringV2/constants';
import { checkoutDismissed } from 'features/productGrowth/components/CheckoutDrawer/slice';

import { toI18n } from 'util/i18n';
import { EVENT_ACTIONS, TRACK_ACTION_TYPES } from 'util/tracking_constants';
import { useTrackUxOnMount } from 'util/uxEvents';

const HiringTieredOptions = ({
  tiers,
  selectedTier,
  handleSelectPlan,
  handleClickTier,
  selectedPeriod,
  handleSelectPeriod,
  pillBelowAnnualPrice,
}: {
  tiers: Record<any, any>;
  selectedTier: string;
  handleSelectPlan: () => void;
  handleClickTier: (key: string) => void;
  selectedPeriod: string;
  handleSelectPeriod: (e: InputChangeEvent) => void;
  pillBelowAnnualPrice?: string;
}) => {
  const dispatch = useDispatch();

  useTrackUxOnMount(EVENT_ACTIONS.SIDE_DRAWER_VIEWED, TRACK_ACTION_TYPES.VIEW);

  const handleClickLetsTalk = useCallback(() => {
    dispatch(checkoutDismissed());
    dispatch(showModal(HIRING_V2_SCHEDULE_CALL_MODAL));
  }, [dispatch]);

  return (
    <Box mv={12} ph={32} flex column>
      <Box gap={20} column>
        <Text
          variant="heading2"
          i18n="checkout_drawer.hiring_assistant.tier_header"
        />
        <Text
          variant="body"
          color="mono900"
          i18n="checkout_drawer.hiring_assistant.tier_subheader"
        />
        {Object.keys(tiers).map(key => (
          <CardSelectable
            key={key}
            w="100%"
            selected={selectedTier === key}
            /* eslint-disable-next-line react-perf/jsx-no-new-function-as-prop */
            onClick={() => handleClickTier(key)}
            content={
              <>
                <Box flex column>
                  <Box mb={8} row>
                    <Text variant="bodyBoldTight">{tiers[key].title}</Text>
                    &nbsp;&nbsp;
                    {tiers[key].pillText && <Pill>{tiers[key].pillText}</Pill>}
                  </Box>
                  <Text variant="bodySmTight" color="mono700">
                    {tiers[key].subtitle}
                  </Text>
                </Box>
                {!!tiers[key].pillText && (
                  <Box mt={16}>
                    <Select
                      name="planType"
                      value={selectedPeriod}
                      options={[
                        {
                          value: 'annual',
                          label: toI18n(
                            'checkout_drawer.hiring_assistant.annual'
                          ),
                        },
                        {
                          value: 'monthly',
                          label: toI18n(
                            'checkout_drawer.hiring_assistant.monthly'
                          ),
                        },
                      ]}
                      onChange={handleSelectPeriod}
                    />
                  </Box>
                )}
                <Box row mt={16}>
                  <Text
                    variant="heading3"
                    strikethrough={
                      selectedPeriod === 'annual' && !!tiers[key].pillText
                    }
                  >{`$${tiers[key].priceInCents / 100}`}</Text>
                  &nbsp;
                  {selectedPeriod === 'annual' && !!tiers[key].pillText && (
                    <Text variant="heading3" color="success300">
                      $99
                    </Text>
                  )}
                  <Box ml={4} mt={2} flex alignItemsCenter>
                    <Text
                      variant="bodySmTight"
                      color="mono700"
                      i18n="checkout_drawer.hiring_assistant.pricing_desc"
                    />
                  </Box>
                </Box>
                <Box mt={4}>
                  {key === 'UNLIMITED' && pillBelowAnnualPrice && (
                    <Pill variant="highlight">{pillBelowAnnualPrice}</Pill>
                  )}
                </Box>
                <Box flex column mt={16}>
                  <Text
                    variant="bodySmTight"
                    i18n="checkout_drawer.hiring_assistant.includes"
                  />
                  <Box mt={8}>
                    <List
                      data={tiers[key].perks}
                      gapOverride="4px"
                      size="small"
                    />
                  </Box>
                </Box>
              </>
            }
          />
        ))}
      </Box>
      <Box column flex gap={20} mt={20}>
        <Box row alignItemsCenter mt="auto" ml="auto" mr="auto">
          <Text i18n="checkout_drawer.hiring_assistant.more_than_2" />
          &nbsp;&nbsp;
          <Button variant="tertiaryPlain" onClick={handleClickLetsTalk}>
            {toI18n('checkout_drawer.hiring_assistant.let_us_talk')}
          </Button>
        </Box>
        <Button onClick={handleSelectPlan}>
          {toI18n('checkout_drawer.hiring_assistant.select_plan')}
        </Button>
      </Box>
    </Box>
  );
};

export default HiringTieredOptions;
